import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Title, Text, CenteredColumn } from '@/shared/ui';
import { StartOverModal } from '@/components/StartOverModal';
import { useApiError } from '@/context/ApiErrorContext';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { apiClient, DEFAULT_CUFF_SIZE } from '@/services/api';
import { US_STATES, validateStateWithZipCode } from '@/utils/state';
import { RgFieldRow, RgTextField, RgSelectField, RgBody, RgFoot } from '@/shared/borisFields';
import { PhaseProgressBar } from '@/components/PhaseProgressBar';
import { phaseForRoute } from '@/consts/phases';
import type { ShippingData } from '@/types/registration';
import { VIEW_SHIPPING, SHIPPING_SUBMIT } from '@/consts/events';

// Assets
import ShippingImage from '@/assets/images/Shipping/shipping.svg';

// Boris `.rg-screen`: fills the frame's main column (flex:1/min-height:0
// against Layout's FlowMain) and splits into RgBody (scrolls) + RgFoot (pinned
// flex sibling); see borisFields.tsx for why that split replaces the retired
// `position:sticky` foot.
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  gap: ${Spacing.x1_5}px;
`;

// Boris's monitor screen leaves a large image slot between the subtitle and the
// form. The stock lifestyle photo isn't licensed, so we keep the existing
// shipping illustration centered in that slot (documented cosmetic deviation).
const SlotImage = styled.img`
  width: 160px;
  height: 160px;
  align-self: center;
`;

const DetailsLabel = styled.div`
  font-weight: 700;
  font-size: 17px;
  color: ${BorisScreener.ink};
  text-align: left;
  width: 100%;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
`;

const Spinner = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  display: inline-block;
  animation: rg-spin 0.8s linear infinite;
  @keyframes rg-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Cta = styled.button<{ $disabled: boolean }>`
  align-self: flex-start;
  width: 100%;
  max-width: 340px;
  height: 72px;
  border-radius: 32px;
  border: none;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  background: ${({ $disabled }) => ($disabled ? '#a9b6ae' : BorisScreener.btnPrimary)};
  transition: background 0.16s ease;

  &:hover {
    background: ${({ $disabled }) => ($disabled ? '#a9b6ae' : BorisScreener.btnPrimaryHover)};
  }
`;

const ShippingStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registrationData, updateShipping, setHasFinishedShipping } = useRegistration();
  const { trackView, track } = useAnalytics();
  const { showApiError } = useApiError();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ShippingData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof ShippingData, boolean>>>({});
  const [showStartOverModal, setShowStartOverModal] = useState(false);

  const data = registrationData.shipping;
  const userData = registrationData.userData;

  // Integrate useAppFlow with back navigation blocking
  const { navigateToNextStep, resetBlocker } = useAppFlow(STEP_KEYS.SHIPPING, {
    onBackNavigationBlocked: () => {
      setShowStartOverModal(true);
    },
  });

  useEffect(() => {
    trackView({ event: VIEW_SHIPPING });
  }, [trackView]);

  // Prefill first name and last name from medicare coverage if available.
  // Mount-only: seeds the shipping name from medicare coverage once.
  useEffect(() => {
    if (!data.firstName && registrationData.medicareCoverage.firstName) {
      updateShipping({ firstName: registrationData.medicareCoverage.firstName });
    }
    if (!data.lastName && registrationData.medicareCoverage.lastName) {
      updateShipping({ lastName: registrationData.medicareCoverage.lastName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateField = useCallback(
    (field: keyof ShippingData, value: string): string | null => {
      switch (field) {
        case 'firstName':
          return !value.trim() ? t('form.validations.first-name') : null;
        case 'lastName':
          return !value.trim() ? t('form.validations.last-name') : null;
        case 'streetAddress':
          return !value.trim() ? t('form.validations.address') : null;
        case 'city':
          if (!value.trim()) return t('form.validations.city');
          if (!/^(?=.*[a-zA-Z])[a-zA-Z\s'.-]+$/u.test(value)) return t('form.validations.city');
          return null;
        case 'state':
          return !value ? t('form.validations.state') : null;
        case 'zipCode':
          if (!value) return t('form.validations.zip-code');
          if (value.length !== 5 || !/^[0-9]+$/.test(value)) return t('form.validations.zip-code');
          if (data.state && !validateStateWithZipCode(data.state, value)) {
            return t('form.validations.zip-code-mismatch');
          }
          return null;
        case 'apartment':
          return null; // Optional field
        default:
          return null;
      }
    },
    [t, data.state],
  );

  const handleBlur = (field: keyof ShippingData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, data[field]);
    setErrors((prev) => ({ ...prev, [field]: error || undefined }));
  };

  const handleChange = (field: keyof ShippingData, value: string) => {
    updateShipping({ [field]: value });
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error || undefined }));
    }
    // Re-validate zip code when state changes
    if (field === 'state' && data.zipCode && touched.zipCode) {
      const zipError = validateField('zipCode', data.zipCode);
      setErrors((prev) => ({ ...prev, zipCode: zipError || undefined }));
    }
  };

  const validateForm = (): boolean => {
    const fieldsToValidate: (keyof ShippingData)[] = [
      'firstName',
      'lastName',
      'streetAddress',
      'city',
      'state',
      'zipCode',
    ];

    const newErrors: Partial<Record<keyof ShippingData, string>> = {};
    let isValid = true;

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, data[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched({
      firstName: true,
      lastName: true,
      streetAddress: true,
      city: true,
      state: true,
      zipCode: true,
    });

    return isValid;
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    if (!validateForm()) return;

    track({ event: SHIPPING_SUBMIT });
    setIsLoading(true);

    try {
      if (!userData) {
        throw new Error('User data is missing');
      }

      await apiClient.shippingDetails(
        {
          firstName: data.firstName,
          lastName: data.lastName,
          streetaddress: [data.streetAddress, data.apartment].filter(Boolean).join(' '),
          city: data.city,
          state: data.state,
          zipcode: data.zipCode,
          phonenumber: userData.phonenumber || registrationData.account.phoneNumber,
          cuffSize: DEFAULT_CUFF_SIZE,
          solutions: userData.solutions?.map((s) => s.solutionType) || ['BP'],
        },
        userData,
      );

      setHasFinishedShipping(true);
      navigateToNextStep();
    } catch (error) {
      // Show API error modal with custom title/subtitle
      showApiError({
        titleOverride: t('shipping.error-title'),
        subtitleOverride: t('shipping.error-subtitle'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOver = () => {
    // Navigate to landing page (which will clear all data on mount)
    setShowStartOverModal(false);
    navigate('/');
  };

  const isFormValid =
    !validateField('firstName', data.firstName) &&
    !validateField('lastName', data.lastName) &&
    !validateField('streetAddress', data.streetAddress) &&
    !validateField('city', data.city) &&
    !validateField('state', data.state) &&
    !validateField('zipCode', data.zipCode);

  const submitVariant = isLoading ? 'loading' : isFormValid ? 'brand-primary' : 'disabled';

  const stateOptions = US_STATES.map((state) => ({ value: state, label: state }));

  return (
    <Wrapper>
      <RgBody>
        <PhaseProgressBar phase={phaseForRoute('/shipping')} />
        <CenteredColumn span={12}>
          <HeadingContainer>
            <Title size="h2">{t('shipping.title')}</Title>
            <Text size="p1">{t('shipping.subtitle')}</Text>
          </HeadingContainer>
        </CenteredColumn>

        <SlotImage src={ShippingImage} alt="" aria-hidden="true" />

        <DetailsLabel>{t('shipping.shipping-details')}</DetailsLabel>

        <Fields>
          <RgFieldRow>
            <RgTextField
              name="firstName"
              label={t('form.first-name')}
              value={data.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              onBlur={() => handleBlur('firstName')}
              autoComplete="given-name"
              error={errors.firstName}
            />
            <RgTextField
              name="lastName"
              label={t('form.last-name')}
              value={data.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              autoComplete="family-name"
              error={errors.lastName}
            />
          </RgFieldRow>

          <RgTextField
            name="streetAddress"
            label={t('form.address')}
            value={data.streetAddress}
            onChange={(e) => handleChange('streetAddress', e.target.value)}
            onBlur={() => handleBlur('streetAddress')}
            autoComplete="address-line1"
            error={errors.streetAddress}
          />

          <RgFieldRow>
            <RgTextField
              name="apartment"
              label={t('form.apartment')}
              value={data.apartment}
              onChange={(e) => handleChange('apartment', e.target.value)}
              onBlur={() => handleBlur('apartment')}
              autoComplete="address-line2"
              placeholder="E.g. 200 E Main St, apt 6. No PO Box."
            />
            <RgTextField
              name="city"
              label={t('form.city')}
              value={data.city}
              onChange={(e) => handleChange('city', e.target.value)}
              onBlur={() => handleBlur('city')}
              autoComplete="address-level2"
              error={errors.city}
            />
          </RgFieldRow>

          <RgFieldRow>
            <RgSelectField
              name="state"
              label={t('form.state')}
              value={data.state}
              placeholder={t('general.select')}
              options={stateOptions}
              onChange={(e) => handleChange('state', e.target.value)}
              onBlur={() => handleBlur('state')}
              error={errors.state}
            />
            <RgTextField
              name="zipCode"
              label={t('form.zip-code')}
              value={data.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
              onBlur={() => handleBlur('zipCode')}
              inputMode="numeric"
              autoComplete="postal-code"
              error={errors.zipCode}
            />
          </RgFieldRow>
        </Fields>
      </RgBody>

      <RgFoot>
        <Cta
          type="button"
          data-testid="submit-button"
          data-variant={submitVariant}
          $disabled={submitVariant === 'disabled'}
          aria-disabled={submitVariant !== 'brand-primary'}
          aria-label={t('shipping.cta')}
          onClick={handleSubmit}
        >
          {isLoading ? <Spinner aria-hidden="true" /> : t('shipping.cta')}
        </Cta>
      </RgFoot>

      {/* Start Over Modal */}
      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={handleStartOver}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />
    </Wrapper>
  );
};

export { ShippingStep };
